import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Text,
  Card,
  Badge,
  SearchInput,
  FilterChips,
  StatusDot,
  EmptyState,
} from '@teals/ui-web';

type AgentScope = 'global' | 'organisation' | 'partner';
type AgentVersion = 'draft' | 'sandbox' | 'published';

interface Agent {
  id: string;
  name: string;
  description: string;
  scope: AgentScope;
  version: AgentVersion;
  updatedAt: string;
}

const MOCK_AGENTS: Agent[] = [
  {
    id: 'agent-1',
    name: 'Customer Support',
    description: 'Handles customer inquiries, ticket routing, and FAQ responses with context-aware conversations.',
    scope: 'global',
    version: 'published',
    updatedAt: '2 hours ago',
  },
  {
    id: 'agent-2',
    name: 'Document Analyst',
    description: 'Extracts key information from uploaded documents and provides structured summaries.',
    scope: 'organisation',
    version: 'published',
    updatedAt: '1 day ago',
  },
  {
    id: 'agent-3',
    name: 'Sales Assistant',
    description: 'Qualifies leads, answers product questions, and schedules demos with potential customers.',
    scope: 'organisation',
    version: 'sandbox',
    updatedAt: '3 hours ago',
  },
  {
    id: 'agent-4',
    name: 'Code Reviewer',
    description: 'Reviews pull requests, suggests improvements, and checks for common security vulnerabilities.',
    scope: 'partner',
    version: 'draft',
    updatedAt: '5 hours ago',
  },
  {
    id: 'agent-5',
    name: 'Onboarding Guide',
    description: 'Walks new users through platform setup, configuration, and first-use tutorials.',
    scope: 'global',
    version: 'sandbox',
    updatedAt: '12 hours ago',
  },
  {
    id: 'agent-6',
    name: 'Data Pipeline Monitor',
    description: 'Monitors data ingestion pipelines, alerts on failures, and provides diagnostic summaries.',
    scope: 'partner',
    version: 'published',
    updatedAt: '30 min ago',
  },
];

const SCOPE_BADGES: Record<AgentScope, { variant: 'primary' | 'info' | 'warning'; label: string }> = {
  global: { variant: 'primary', label: 'Global' },
  organisation: { variant: 'info', label: 'Organisation' },
  partner: { variant: 'warning', label: 'Partner' },
};

const FILTER_CHIPS = [
  { id: 'all', label: 'All' },
  { id: 'global', label: 'Global' },
  { id: 'organisation', label: 'Organisation' },
  { id: 'partner', label: 'Partner' },
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const AgentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const AgentCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border);
`;

const UpdatedText = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const Agents: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');

  const filteredAgents = useMemo(() => {
    return MOCK_AGENTS.filter((agent) => {
      const matchesSearch =
        !search || agent.name.toLowerCase().includes(search.toLowerCase());
      const matchesScope = scopeFilter === 'all' || agent.scope === scopeFilter;
      return matchesSearch && matchesScope;
    });
  }, [search, scopeFilter]);

  const chipsWithCounts = useMemo(
    () =>
      FILTER_CHIPS.map((chip) => ({
        ...chip,
        count:
          chip.id === 'all'
            ? MOCK_AGENTS.length
            : MOCK_AGENTS.filter((a) => a.scope === chip.id).length,
      })),
    []
  );

  return (
    <Page>
      <Header>
        <Text variant="h3">Agents</Text>
        <Button variant="primary" onClick={() => navigate('/agents/new')}>
          Create Agent
        </Button>
      </Header>

      <Toolbar>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search agents..."
        />
        <FilterChips
          items={chipsWithCounts}
          activeId={scopeFilter}
          onChange={setScopeFilter}
        />
      </Toolbar>

      {filteredAgents.length === 0 ? (
        <EmptyState
          title="No agents found"
          description={
            search
              ? `No agents match "${search}". Try adjusting your search or filters.`
              : 'No agents in this category yet. Create your first agent to get started.'
          }
          action={
            search ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setScopeFilter('all');
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={() => navigate('/agents/new')}>
                Create Agent
              </Button>
            )
          }
        />
      ) : (
        <AgentGrid>
          {filteredAgents.map((agent) => (
            <AgentCard
              key={agent.id}
              padding={20}
              elevation="sm"
              onClick={() => navigate(`/agents/${agent.id}`)}
            >
              <CardHeader>
                <Text variant="h5" style={{ margin: 0 }}>
                  {agent.name}
                </Text>
                <Badge variant={SCOPE_BADGES[agent.scope].variant} size="sm">
                  {SCOPE_BADGES[agent.scope].label}
                </Badge>
              </CardHeader>
              <CardDescription>{agent.description}</CardDescription>
              <CardFooter>
                <StatusDot status={agent.version} label={agent.version} />
                <UpdatedText>{agent.updatedAt}</UpdatedText>
              </CardFooter>
            </AgentCard>
          ))}
        </AgentGrid>
      )}
    </Page>
  );
};

export default Agents;
